import type { CallTranscriptMessage } from "@expert/shared-types";

import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { useEffect, useEffectEvent, useRef } from "react";

import { isVoiceTask, useActiveTask, useSession } from "@/sdk";

// Ignore calls shorter than this: a quick hangup can end before the first snippet round-trips.
const MIN_EXPECTED_CALL_MS = 15_000;

const logger = getLogger({ module: "useTranscriptDeliveryMonitor" });

type CallRecord = { sid: string; expected: boolean; received: boolean; startedAt: number };

/**
 * Emits `TranscriptNeverArrived` when a call that was expected to have a live transcript ends having
 * received zero snippets (upstream sent nothing) — the counterpart to `useTranscriptSnippetListener`,
 * which only catches snippets that arrive and fail to parse.
 *
 * Called once at the workspace level, not in the transcript component, which remounts at call end and
 * would lose the per-call state. It only emits on a real call-end transition, never on unmount.
 */
export function useTranscriptDeliveryMonitor(isVoiceWorkspaceRedesignV2Enabled: boolean): void {
    const activeTask = useActiveTask();
    const session = useSession();

    const activeVoiceTask = isVoiceTask(activeTask) ? activeTask : null;
    const activeCallSid = activeVoiceTask?.status === "assigned" ? activeVoiceTask.agentCallId : undefined;

    // A transcript is expected on the redesigned voice workspace — where the transcript panel is always
    // shown — for a real customer call. This is the surface we monitor; the legacy partner flag
    // isRealtimeTranscriptEnabled does not gate the redesign transcript. `with-customer` excludes idle and
    // Expert University sims (which are without-customer).
    const transcriptExpected = Boolean(
        activeCallSid && session.kind === "with-customer" && isVoiceWorkspaceRedesignV2Enabled,
    );

    const activeCallRef = useRef<CallRecord | null>(null);
    const emittedRef = useRef<Set<string>>(new Set());

    const onSnippet = useEffectEvent((event: GaiaWebSocketResponse) => {
        const record = activeCallRef.current;
        if (!record) return;
        // Frames can be malformed/schema-drifted (the failure this feature complements), so guard the
        // dereference — a throw here would propagate into the event-bus dispatch.
        const body = event.body as Partial<CallTranscriptMessage> | undefined;
        if (!body || body.sessionId !== record.sid) return;
        if (!body.transcripts || body.transcripts.length === 0) return;
        record.received = true;
    });

    // `expected` is snapshotted when the call becomes active (below), but the redesign flag and
    // session.kind can resolve a render later — e.g. a mid-call reload, where activeCallSid is defined
    // immediately but the rest hasn't settled. Latch it up so late resolution isn't a false negative;
    // it never latches down.
    useEffect(() => {
        if (transcriptExpected && activeCallRef.current) activeCallRef.current.expected = true;
    }, [transcriptExpected]);

    // A changed `activeCallSid` is a real call-end edge, so the previous call is finalized here in the
    // effect body — NOT in cleanup, which also runs on unmount (page reload / workspace teardown) and
    // would emit for a call that is still ongoing.
    useEffect(() => {
        const ended = activeCallRef.current;
        if (
            ended?.expected &&
            !ended.received &&
            Date.now() - ended.startedAt >= MIN_EXPECTED_CALL_MS &&
            !emittedRef.current.has(ended.sid)
        ) {
            emittedRef.current.add(ended.sid);
            logger.error({ callSid: ended.sid }, "Transcript | expected transcript but no snippets arrived");
            void sendErrorMetric("TranscriptNeverArrived");
        }

        activeCallRef.current = activeCallSid
            ? { sid: activeCallSid, expected: transcriptExpected, received: false, startedAt: Date.now() }
            : null;
        // oxlint-disable-next-line exhaustive-deps
    }, [activeCallSid]);

    useEffect(() => gaiaWsEventBus.on("gaia_ws_call-transcript", onSnippet), []);
}
